import { useState, useEffect, useCallback } from 'react';
import { fetchStatisticsFromUrl } from '@/lib/api';
import type { Statistics } from '@/types/statistics';

interface UseStatisticsFromUrlResult {
  data: Statistics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetches + Zod-validates statistics from an arbitrary URL.
 * Used by tournament mode where the JSON URL (with SAS token) is passed
 * in via query parameter.
 */
export function useStatisticsFromUrl(
  url: string | null,
): UseStatisticsFromUrlResult {
  const [data, setData] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!url) {
      setLoading(false);
      setError(new Error('Missing tournament JSON URL'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const stats = await fetchStatisticsFromUrl(url);
      setData(stats);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}
